import React, { useState, useEffect, useMemo } from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import { t } from '../utils/translations';
import api from '../utils/api';
import { X, Calendar, Filter, MapPin, ChevronLeft, ChevronRight } from 'lucide-react';
import PageMeta from '../components/PageMeta';

const Gallery = () => {
  const { language } = useLanguage();
  const [photos, setPhotos] = useState([]);
  const [selectedPhoto, setSelectedPhoto] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedDate, setSelectedDate] = useState('all');
  const [selectedLocation, setSelectedLocation] = useState('all');
  const [displayCount, setDisplayCount] = useState(12); // Show 12 photos initially

  useEffect(() => {
    fetchGallery();
  }, []);

  const fetchGallery = async () => {
    try {
      const response = await api.get('/gallery');
      setPhotos(response.data);
    } catch (error) {
      console.error('Error fetching gallery:', error);
      // No mock data - display actual gallery photos only
      setPhotos([]);
    } finally {
      setLoading(false);
    }
  };

  // Get unique dates from photos
  const availableDates = useMemo(() => {
    const dates = photos.map(photo => {
      const date = new Date(photo.date);
      return date.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
    });
    return ['all', ...new Set(dates)];
  }, [photos]);

  // Get unique locations from photos
  const availableLocations = useMemo(() => {
    const locations = photos
      .map(photo => photo.location || '')
      .filter(loc => loc.trim() !== '');
    return ['all', ...new Set(locations)];
  }, [photos]);

  // Filter photos by selected date and location
  const filteredPhotos = useMemo(() => {
    return photos.filter(photo => {
      const photoDate = new Date(photo.date).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
      const dateMatch = selectedDate === 'all' || photoDate === selectedDate;
      const locationMatch = selectedLocation === 'all' || photo.location === selectedLocation;
      return dateMatch && locationMatch;
    });
  }, [photos, selectedDate, selectedLocation]);

  // Navigation functions for lightbox
  const getCurrentPhotoIndex = () => {
    return filteredPhotos.findIndex(photo => photo.id === selectedPhoto?.id);
  };

  const goToNextPhoto = () => {
    const currentIndex = getCurrentPhotoIndex();
    const nextIndex = (currentIndex + 1) % filteredPhotos.length; // Loop to start
    setSelectedPhoto(filteredPhotos[nextIndex]);
  };

  const goToPreviousPhoto = () => {
    const currentIndex = getCurrentPhotoIndex();
    const prevIndex = currentIndex === 0 ? filteredPhotos.length - 1 : currentIndex - 1; // Loop to end
    setSelectedPhoto(filteredPhotos[prevIndex]);
  };

  // Handle keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (!selectedPhoto) return;
      
      if (e.key === 'ArrowRight') {
        goToNextPhoto();
      } else if (e.key === 'ArrowLeft') {
        goToPreviousPhoto();
      } else if (e.key === 'Escape') {
        setSelectedPhoto(null);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [selectedPhoto, filteredPhotos]);

  // Paginated photos
  const displayedPhotos = useMemo(() => {
    return filteredPhotos.slice(0, displayCount);
  }, [filteredPhotos, displayCount]);

  // Load more function
  const loadMore = () => {
    setDisplayCount(prev => prev + 12);
  };

  // Reset display count when filters change
  useEffect(() => {
    setDisplayCount(12);
  }, [selectedDate, selectedLocation]);

  return (


    <>


      <PageMeta 


        title="Photo Gallery"


        path="/gallery"


        description="View photos from our church events, celebrations, and community gatherings. Memories of worship and fellowship."


      />
    <div className="min-h-screen pt-20">
      {/* Header */}
      <section className="bg-gradient-to-br from-amber-100 via-orange-50 to-rose-100 dark:from-gray-900 dark:via-gray-800 dark:to-gray-900 py-16">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-4xl md:text-5xl font-bold text-gray-900 dark:text-white mb-4" style={{fontFamily: 'Playfair Display, serif'}}>
            {t(language, 'gallery.title')}
          </h1>
          <p className="text-lg text-gray-700 dark:text-gray-300">
            {t(language, 'gallery.subtitle')}
          </p>
        </div>
      </section>

      {/* Gallery Grid */}
      <section className="py-16 bg-white dark:bg-gray-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Date and Location Filters */}
          {!loading && photos.length > 0 && (
            <div className="mb-8 space-y-4">
              <div className="flex items-center space-x-2 text-gray-700 dark:text-gray-300">
                <Filter className="w-5 h-5" />
                <span className="font-medium text-lg">
                  {t(language, 'gallery.filterPhotos')}
                </span>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {/* Date Filter */}
                <div className="flex items-center space-x-3">
                  <Calendar className="w-5 h-5 text-amber-600 dark:text-amber-500 flex-shrink-0" />
                  <select
                    value={selectedDate}
                    onChange={(e) => setSelectedDate(e.target.value)}
                    className="flex-1 px-4 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-amber-500 focus:border-amber-500 transition-colors"
                    data-testid="date-filter-select"
                  >
                    {availableDates.map((date) => (
                      <option key={date} value={date}>
                        {date === 'all' 
                          ? t(language, 'gallery.allDates')
                          : date
                        }
                      </option>
                    ))}
                  </select>
                </div>

                {/* Location Filter */}
                <div className="flex items-center space-x-3">
                  <MapPin className="w-5 h-5 text-amber-600 dark:text-amber-500 flex-shrink-0" />
                  <select
                    value={selectedLocation}
                    onChange={(e) => setSelectedLocation(e.target.value)}
                    className="flex-1 px-4 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-amber-500 focus:border-amber-500 transition-colors"
                    data-testid="location-filter-select"
                  >
                    {availableLocations.map((location) => (
                      <option key={location} value={location}>
                        {location === 'all' 
                          ? t(language, 'gallery.allLocations')
                          : location
                        }
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              {/* Clear All Filters Button */}
              {(selectedDate !== 'all' || selectedLocation !== 'all') && (
                <div className="flex justify-end">
                  <button
                    onClick={() => {
                      setSelectedDate('all');
                      setSelectedLocation('all');
                    }}
                    className="px-6 py-2 text-sm bg-amber-600 hover:bg-amber-700 text-white rounded-lg transition-colors flex items-center space-x-2"
                    data-testid="clear-all-filters-btn"
                  >
                    <X className="w-4 h-4" />
                    <span>{t(language, 'gallery.clearFilters')}</span>
                  </button>
                </div>
              )}
            </div>
          )}

          {loading ? (
            <div className="text-center py-20">
              <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-amber-600"></div>
            </div>
          ) : filteredPhotos.length > 0 ? (
            <>
              <div className="mb-4 text-sm text-gray-600 dark:text-gray-400">
                {language === 'en' 
                  ? `Showing ${displayedPhotos.length} of ${filteredPhotos.length} photo${filteredPhotos.length !== 1 ? 's' : ''}`
                  : `${displayedPhotos.length} ካብ ${filteredPhotos.length} ስእልታት`
                }
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                {displayedPhotos.map((photo) => (
                <div
                  key={photo.id}
                  onClick={() => setSelectedPhoto(photo)}
                  className="group relative bg-gray-100 dark:bg-gray-800 rounded-xl shadow-lg hover:shadow-2xl transition-all duration-300 cursor-pointer flex items-center justify-center"
                  data-testid={`photo-${photo.id}`}
                  style={{ minHeight: '400px', height: 'auto' }}
                >
                  <img
                    src={photo.image_url}
                    alt={language === 'en' ? photo.title_en : photo.title_ti}
                    loading="lazy"
                    className="w-full h-auto object-contain transition-transform duration-300 group-hover:scale-105 rounded-xl"
                    style={{ maxHeight: '600px' }}
                  />
                  <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 via-black/40 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 rounded-b-xl">
                    <div className="p-6 w-full">
                      <h3 className="text-white font-bold text-lg mb-1">
                        {language === 'en' ? photo.title_en : photo.title_ti}
                      </h3>
                      <p className="text-gray-200 text-sm">
                        {language === 'en' ? photo.description_en : photo.description_ti}
                      </p>
                    </div>
                  </div>
                </div>
              ))}
              </div>

              {/* Load More Button */}
              {displayedPhotos.length < filteredPhotos.length && (
                <div className="text-center mt-12">
                  <button
                    onClick={loadMore}
                    className="px-8 py-3 bg-amber-600 hover:bg-amber-700 text-white rounded-full font-semibold transition-all duration-300 shadow-lg hover:shadow-xl"
                  >
                    {language === 'en' 
                      ? `Load More (${filteredPhotos.length - displayedPhotos.length} remaining)` 
                      : `ተወሳኺ ስእልታት (${filteredPhotos.length - displayedPhotos.length} ዝተረፈ)`
                    }
                  </button>
                </div>
              )}
            </>
          ) : photos.length > 0 ? (
            <div className="text-center py-20">
              <Filter className="w-16 h-16 text-gray-400 mx-auto mb-4" />
              <p className="text-gray-600 dark:text-gray-400 text-lg mb-2">
                {language === 'en' ? 'No photos found with selected filters' : 'ብዝተመረጸ ምርጫ ስእልታት የለን'}
              </p>
              <button
                onClick={() => {
                  setSelectedDate('all');
                  setSelectedLocation('all');
                }}
                className="mt-4 px-6 py-2 bg-amber-600 hover:bg-amber-700 text-white rounded-full transition-colors"
              >
                {language === 'en' ? 'Show All Photos' : 'ኩሉ ስእልታት ርአዩ'}
              </button>
            </div>
          ) : (
            <div className="text-center py-20">
              <p className="text-gray-600 dark:text-gray-400 text-lg">
                {language === 'en' ? 'No photos available' : 'ስእሊታት የለን'}
              </p>
            </div>
          )}
        </div>
      </section>

      {/* Lightbox Modal */}
      {selectedPhoto && (
        <div
          className="fixed inset-0 z-50 bg-black/95 flex items-center justify-center p-4"
          onClick={() => setSelectedPhoto(null)}
          data-testid="lightbox-modal"
        >
          {/* Close Button */}
          <button
            onClick={() => setSelectedPhoto(null)}
            className="absolute top-4 right-4 text-white hover:text-gray-300 transition-colors z-10"
            data-testid="close-lightbox-btn"
            aria-label="Close"
          >
            <X className="w-8 h-8" />
          </button>

          {/* Previous Button */}
          {filteredPhotos.length > 1 && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                goToPreviousPhoto();
              }}
              className="absolute left-4 top-1/2 -translate-y-1/2 bg-white/10 hover:bg-white/20 text-white p-3 rounded-full transition-all z-10 backdrop-blur-sm"
              data-testid="prev-photo-btn"
              aria-label="Previous photo"
            >
              <ChevronLeft className="w-8 h-8" />
            </button>
          )}

          {/* Next Button */}
          {filteredPhotos.length > 1 && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                goToNextPhoto();
              }}
              className="absolute right-4 top-1/2 -translate-y-1/2 bg-white/10 hover:bg-white/20 text-white p-3 rounded-full transition-all z-10 backdrop-blur-sm"
              data-testid="next-photo-btn"
              aria-label="Next photo"
            >
              <ChevronRight className="w-8 h-8" />
            </button>
          )}

          {/* Image Container */}
          <div className="max-w-6xl w-full" onClick={(e) => e.stopPropagation()}>
            <img
              src={selectedPhoto.image_url}
              alt={language === 'en' ? selectedPhoto.title_en : selectedPhoto.title_ti}
              className="w-full h-auto rounded-lg shadow-2xl"
            />
            <div className="mt-6 text-center">
              <h2 className="text-2xl font-bold text-white mb-2">
                {language === 'en' ? selectedPhoto.title_en : selectedPhoto.title_ti}
              </h2>
              <p className="text-gray-300">
                {language === 'en' ? selectedPhoto.description_en : selectedPhoto.description_ti}
              </p>
              {/* Photo Counter */}
              <p className="text-gray-400 text-sm mt-3">
                {getCurrentPhotoIndex() + 1} / {filteredPhotos.length}
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
    </>
  );
};

export default Gallery;
